import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { CalendarEntity } from "../services/calendar/calendar.types";

dayjs.extend(utc);

// ============= FUNCIONES UTC =============

export const parseUTCDate = (dateString: string): dayjs.Dayjs => {
  return dayjs.utc(dateString);
};

export const toUTCDate = (date: dayjs.Dayjs): dayjs.Dayjs => {
  return dayjs.utc(date.format("YYYY-MM-DD"));
};

export const toUTCISOString = (date: dayjs.Dayjs): string => {
  return toUTCDate(date).toISOString();
};

// ============= FUNCIONES DE VALIDACIÓN =============

export const getOrderErrorWeeks = (calendarData: CalendarEntity[]): number[] => {
  const orderErrors = new Set<number>();
  const sortedWeeks = [...calendarData].sort((a, b) => a.week - b.week);

  for (let i = 0; i < sortedWeeks.length - 1; i++) {
    const currentWeek = sortedWeeks[i];
    const nextWeek = sortedWeeks[i + 1];

    const currentDate = parseUTCDate(currentWeek.start_date);
    const nextDate = parseUTCDate(nextWeek.start_date);

    // Si la semana actual tiene fecha mayor o igual a la siguiente, hay error
    if (
      currentDate.isAfter(nextDate) ||
      currentDate.isSame(nextDate, "day")
    ) {
      orderErrors.add(currentWeek.week);
      orderErrors.add(nextWeek.week);
    }
  }

  return Array.from(orderErrors).sort((a, b) => a - b);
};

// ============= FUNCIÓN DE FORMATO =============

export const formatDate = (dateString: string): string => {
  return parseUTCDate(dateString).format("DD MMM YY");
};

// ============= FUNCIONES ADICIONALES PARA INDEX.TSX =============

// Función para obtener las semanas que tienen fechas solapadas (versión index.tsx)
export const getOverlappingWeeksAll = (calendar: CalendarEntity[]): number[] => {
  const dateMap = new Map<string, number[]>();
  const overlappingWeeks = new Set<number>();

  // Agrupar semanas por fecha
  calendar.forEach((week) => {
    const dateKey = parseUTCDate(week.start_date).format("YYYY-MM-DD");
    if (!dateMap.has(dateKey)) {
      dateMap.set(dateKey, []);
    }
    dateMap.get(dateKey)!.push(week.week);
  });

  // Encontrar fechas con múltiples semanas
  dateMap.forEach((weeks) => {
    if (weeks.length > 1) {
      weeks.forEach((week) => overlappingWeeks.add(week));
    }
  });

  return Array.from(overlappingWeeks).sort((a, b) => a - b);
};

// Función para detectar solapamientos en las fechas (versión index.tsx)
export const detectOverlapsAll = (calendar: CalendarEntity[]): boolean => {
  const dates = calendar.map((week) => ({
    week: week.week,
    date: parseUTCDate(week.start_date).format("YYYY-MM-DD"),
  }));

  // Buscar fechas duplicadas
  const dateMap = new Map();
  for (const item of dates) {
    if (dateMap.has(item.date)) {
      return true; // Hay solapamiento
    }
    dateMap.set(item.date, item.week);
  }
  return false;
};

// Función para verificar si hay errores en el calendario (versión index.tsx)
export const hasCalendarErrorsAll = (calendar: CalendarEntity[]): boolean => {
  const overlappingWeeks = getOverlappingWeeksAll(calendar);
  const orderErrorWeeks = getOrderErrorWeeks(calendar);
  return overlappingWeeks.length > 0 || orderErrorWeeks.length > 0;
};
